import React, {
  type ReactElement,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import ContentPage from "../contentPage";
import useApiClient from "../../hooks/useApiClient";
import useDataBrowseQueryParams from "../../hooks/useDataBrowseQueryParams";
import useDataBrowseQueries from "../../hooks/useDataBrowseQueries";
import useFileSystemTree from "../../hooks/useFileSystemTree";
import useBrowseInfiniteScroll from "../../hooks/useBrowseInfiniteScroll";
import BrowseHeader from "./browseHeader";
import BrowseControls from "./browseControls";
import BrowseItems from "./browseItems";
import BrowsePreviewDialog from "./browsePreviewDialog";
import BrowseAdvancedSearchModal from "./browseAdvancedSearchModal";
import BrowseNewFolderModal from "./browseNewFolderModal";
import BrowseDeleteModal from "./browseDeleteModal";
import FileUploadManager from "../fileUpload/FileUploadManager";
import UploadProgressTracker, {
  TrackedUpload,
  UploadStage,
} from "../fileUpload/UploadProgressTracker";
import { assetIsPreviewable } from "../preview/utils";
import {
  prepAssetDownloadURL,
  prepAssetPreviewURL,
} from "../../apiUtils/downloadUrls";
import { hasFeatureFlag } from "../../utils";
import { AvailableFeatureFlags } from "../../apiClient";
import { RealAssetType } from "../../apiClient/models/RealAssetType";
import UserContext from "../../contexts/user";
import { useNavigate } from "@tanstack/react-router";
import { mapFileTypeToFileExtensions } from "../dropdowns/fileTypeDropdown";
import { ARCHIVE_SUFFIX, SCROLL_THRESHOLD_PX } from "./constants";
import {
  ensureArchiveRoots,
  mapFilesToFileItems,
  resolveLinkedCruisePath,
} from "./utils";
import {
  AdvancedSearchParams,
  BrowseHistoryEntry,
  FileSystemItem,
} from "./types";

type PreviewNavigationDirection = "previous" | "next" | null;

const getPreviewItemKey = (item: FileSystemItem | null | undefined) =>
  item?.dataAssetPublicId || item?.path || "";

const getPreviewableItems = (items: FileSystemItem[]) =>
  items.filter((item) => {
    const meta = assetIsPreviewable(item);
    return item.type === "file" && !!item.dataAssetPublicId && meta.canPreview;
  });

export default function DataBrowsePage(): ReactElement {
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [newFolderName, setNewFolderName] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [history, setHistory] = useState<BrowseHistoryEntry[]>([
    { path: [], search: "" },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [sortBy, setSortBy] = useState<"name" | "size" | "modified" | "none">(
    "none"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const [showUploadManager, setShowUploadManager] = useState(false);
  const [trackedUploads, setTrackedUploads] = useState<TrackedUpload[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationChecked, setDeleteConfirmationChecked] =
    useState(false);
  const [advancedSearchParams, setAdvancedSearchParams] =
    useState<AdvancedSearchParams>({
      searchTerm: "",
      fileTypes: [],
      dateModified: "any",
      dateFrom: "",
      dateTo: "",
    });
  const [treeError, setTreeError] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<FileSystemItem | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [lastOpenedItemKey, setLastOpenedItemKey] = useState<string | null>(
    null
  );
  const [previewNavigatingDirection, setPreviewNavigatingDirection] =
    useState<PreviewNavigationDirection>(null);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const hasDeploymentFeature =
    user &&
    hasFeatureFlag(user, AvailableFeatureFlags.REAL_ASSET_DEPLOYMENT_DETAIL);

  const api = useApiClient();
  const {
    queryValues,
    setQueryValues,
    searchQuery,
    debouncedSearchQuery,
    activeFilters,
  } = useDataBrowseQueryParams();

  const cruiseQuery = useQuery({
    queryKey: ["cruise-deployments"],
    queryFn: () =>
      api.dataAccess.listRealAssetDeployments({
        realAssetType: RealAssetType.VESSEL,
        size: 500,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const linkedDeploymentPublicId =
    queryValues.realAssetDeploymentPublicId || undefined;
  const linkedManifestType = queryValues.manifestType || undefined;
  const linkedDeploymentQuery = useQuery({
    queryKey: ["data-browse-linked-deployment", linkedDeploymentPublicId],
    queryFn: () =>
      api.dataAccess.realAssetDeploymentDetail({
        publicId: linkedDeploymentPublicId as string,
      }),
    enabled: !!linkedDeploymentPublicId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const linkedDeployment =
    linkedDeploymentQuery.data ||
    cruiseQuery.data?.items?.find(
      (deployment) => deployment.public_id === linkedDeploymentPublicId
    );
  const linkedCruiseId = linkedDeployment
    ? linkedDeployment.external_id ||
      linkedDeployment.name ||
      linkedDeployment.public_id ||
      undefined
    : undefined;

  const seeDeploymentMutation = useMutation({
    mutationFn: (publicId: string) =>
      api.dataAccess.realAssetDeploymentDetail({ publicId }),
    onError: () => {
      setTreeError("Failed to open deployment");
    },
  });

  const isOpeningDeployment = seeDeploymentMutation.isPending;

  const {
    currentCruiseId,
    directoriesQuery,
    filesQuery,
    folderItems,
    isArchive,
    isFetchingNodes,
    isLoadingMore,
  } = useDataBrowseQueries({
    currentPath,
    activeFilters,
    debouncedSearchQuery,
    realAssetDeploymentPublicId: linkedDeploymentPublicId,
  });
  const {
    data: directoriesData,
    fetchNextPage: fetchNextDirectoriesPage,
    hasNextPage: hasNextDirectoriesPage,
    isError: directoriesIsError,
    isFetchingNextPage: isFetchingNextDirectoriesPage,
  } = directoriesQuery;
  const {
    data: filesData,
    fetchNextPage: fetchNextFilesPage,
    hasNextPage: hasNextFilesPage,
    isError: filesIsError,
    isFetchingNextPage: isFetchingNextFilesPage,
  } = filesQuery;

  const { setFileSystemTree, currentFolder } = useFileSystemTree({
    currentPath,
    currentCruiseId,
    folderItems,
    hasFolderData: !!directoriesData || !!filesData,
  });

  useEffect(() => {
    if (!cruiseQuery.data) return;
    const resp = cruiseQuery.data;
    const deployments = [...(resp.items || [])];
    if (
      linkedDeploymentQuery.data &&
      !deployments.some(
        (deployment) =>
          deployment.public_id === linkedDeploymentQuery.data?.public_id
      )
    ) {
      deployments.push(linkedDeploymentQuery.data);
    }
    const cruises = deployments.map((dep) => {
      const cruiseId = dep.external_id || dep.name || dep.public_id || "cruise";
      const cruiseNode: FileSystemItem = {
        name: cruiseId,
        displayName: dep.name || undefined,
        cruiseId,
        realAssetDeploymentPublicId: dep.public_id || undefined,
        path: cruiseId,
        type: "folder",
        children: [],
        modified: dep.deployment_start_dt || undefined,
      };
      return ensureArchiveRoots(cruiseNode);
    });
    const sortedCruises = cruises.sort((a, b) => {
      const yearA = a.modified
        ? new Date(a.modified).getFullYear().toString()
        : "Unknown";
      const yearB = b.modified
        ? new Date(b.modified).getFullYear().toString()
        : "Unknown";
      return yearB.localeCompare(yearA);
    });
    setFileSystemTree((prev) => ({ ...prev, children: sortedCruises }));
  }, [cruiseQuery.data, linkedDeploymentQuery.data, setFileSystemTree]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPath, searchQuery, activeFilters]);

  const hasActiveFilters = useCallback(() => {
    if (!activeFilters) return false;
    return (
      activeFilters.fileTypes.length > 0 || activeFilters.dateModified !== "any"
    );
  }, [activeFilters]);

  useEffect(() => {
    setHistory((prev) => {
      const updated = [...prev];
      if (updated[historyIndex]) {
        updated[historyIndex] = {
          ...updated[historyIndex],
          search: searchQuery,
        };
      }
      return updated;
    });
  }, [searchQuery, historyIndex]);

  useEffect(() => {
    const queryPath = queryValues.path
      ? queryValues.path.split("/").filter(Boolean)
      : [];
    const pathFromQuery = resolveLinkedCruisePath(
      queryPath,
      linkedCruiseId,
      linkedManifestType
    );
    const currentPathString = currentPath.join("/");
    const queryPathString = pathFromQuery.join("/");
    if (currentPathString === queryPathString) return;

    setCurrentPath(pathFromQuery);
    setSelectedItems(new Set());
    if (pathFromQuery.length === 0) {
      setHistoryIndex(0);
      return;
    }
    setHistory([
      { path: [], search: "" },
      {
        path: pathFromQuery,
        search: searchQuery,
        realAssetDeploymentPublicId: linkedDeploymentPublicId,
        manifestType: linkedManifestType,
      },
    ]);
    setHistoryIndex(1);
  }, [
    queryValues.path,
    currentPath,
    searchQuery,
    linkedCruiseId,
    linkedDeploymentPublicId,
    linkedManifestType,
  ]);

  useEffect(() => {
    if (linkedDeploymentQuery.isError) {
      setTreeError("Failed to load cruise");
    } else if (treeError === "Failed to load cruise") {
      setTreeError(null);
    }
  }, [linkedDeploymentQuery.isError, treeError]);

  useEffect(() => {
    if (currentPath.length < 2) return;
    if (directoriesIsError || filesIsError) {
      setTreeError("Failed to load folder");
    } else if (treeError === "Failed to load folder") {
      setTreeError(null);
    }
  }, [currentPath.length, directoriesIsError, filesIsError, treeError]);

  const handleIntersection = useCallback(() => {
    if (hasNextDirectoriesPage && !isFetchingNextDirectoriesPage) {
      fetchNextDirectoriesPage();
    }
    if (hasNextFilesPage && !isFetchingNextFilesPage) {
      fetchNextFilesPage();
    }
  }, [
    fetchNextDirectoriesPage,
    fetchNextFilesPage,
    hasNextDirectoriesPage,
    hasNextFilesPage,
    isFetchingNextDirectoriesPage,
    isFetchingNextFilesPage,
  ]);

  useBrowseInfiniteScroll({
    enabled: currentPath.length >= 2,
    hasMore: !!(hasNextDirectoriesPage || hasNextFilesPage),
    isBusy: isLoadingMore || isFetchingNodes,
    rootMarginPx: SCROLL_THRESHOLD_PX,
    scrollContainerRef,
    sentinelRef: loadMoreSentinelRef,
    onIntersect: handleIntersection,
  });

  const applyAdvancedFilters = useCallback(
    (items: FileSystemItem[]): FileSystemItem[] => {
      if (!activeFilters) return items;

      return items.filter((item) => {
        if (activeFilters.fileTypes.length > 0) {
          if (item.type === "folder") {
            return false;
          }

          const extension = item.name.split(".").pop()?.toLowerCase();
          const [, allowedExtensions] = mapFileTypeToFileExtensions(
            activeFilters.fileTypes
          );

          if (
            !extension ||
            !(allowedExtensions as string[]).includes(extension)
          ) {
            return false;
          }
        }

        return true;
      });
    },
    [activeFilters]
  );

  const sortBrowseItems = useCallback(
    (items: FileSystemItem[]) => {
      const sortedItems = [...items].sort((a, b) => {
        if (sortBy === "none") {
          if (a.type === "folder" && b.type === "file") return -1;
          if (a.type === "file" && b.type === "folder") return 1;
          return 0;
        }

        let comparison = 0;

        switch (sortBy) {
          case "name":
            comparison = a.name.localeCompare(b.name);
            break;
          case "size": {
            const sizeA = a.size || 0;
            const sizeB = b.size || 0;
            comparison = sizeA - sizeB;
            break;
          }
          case "modified": {
            const dateA = a.modified ? new Date(a.modified).getTime() : 0;
            const dateB = b.modified ? new Date(b.modified).getTime() : 0;
            comparison = dateA - dateB;
            break;
          }
        }

        return sortOrder === "asc" ? comparison : -comparison;
      });

      return sortedItems;
    },
    [sortBy, sortOrder]
  );

  const filteredAndSortedItems = useMemo(() => {
    let items: FileSystemItem[] = [];

    if (debouncedSearchQuery && currentPath.length < 2) {
      items = (currentFolder.children || []).filter((item) =>
        item.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
    } else {
      items = currentFolder.children || [];
    }

    return sortBrowseItems(applyAdvancedFilters(items));
  }, [
    currentFolder,
    debouncedSearchQuery,
    sortBrowseItems,
    applyAdvancedFilters,
    currentPath.length,
  ]);

  const itemsByYear = useMemo(() => {
    if (currentPath.length > 0 || searchQuery || hasActiveFilters()) {
      return [{ year: "", items: filteredAndSortedItems }];
    }

    const grouped: Record<string, FileSystemItem[]> = {};

    filteredAndSortedItems.forEach((item) => {
      let year = "Unknown";

      if (item.modified) {
        year = new Date(item.modified).getFullYear().toString();
      } else if (item.created) {
        year = new Date(item.created).getFullYear().toString();
      }

      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(item);
    });

    const sortedYears = Object.keys(grouped).sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return parseInt(b) - parseInt(a);
    });

    const result: Array<{ year: string; items: FileSystemItem[] }> = [];
    sortedYears.forEach((year) => {
      result.push({ year, items: grouped[year] });
    });

    return result;
  }, [filteredAndSortedItems, currentPath, searchQuery, hasActiveFilters]);

  const previewableItems = useMemo(
    () => getPreviewableItems(filteredAndSortedItems),
    [filteredAndSortedItems]
  );

  const previewItemIndex = useMemo(
    () =>
      previewableItems.findIndex(
        (item) => getPreviewItemKey(item) === getPreviewItemKey(previewItem)
      ),
    [previewItem, previewableItems]
  );

  const buildPathQuery = useCallback(
    (folderPath: string[]) => {
      const staysWithinLinkedCruise =
        !!linkedCruiseId && folderPath[0] === linkedCruiseId;
      return {
        path: staysWithinLinkedCruise
          ? folderPath.slice(1).join("/") || "/"
          : folderPath.join("/"),
        realAssetDeploymentPublicId: staysWithinLinkedCruise
          ? queryValues.realAssetDeploymentPublicId
          : "",
        manifestType:
          staysWithinLinkedCruise && folderPath.length > 1
            ? queryValues.manifestType
            : "",
      };
    },
    [
      linkedCruiseId,
      queryValues.manifestType,
      queryValues.realAssetDeploymentPublicId,
    ]
  );

  const buildHistoryQuery = useCallback((entry: BrowseHistoryEntry) => {
    const deploymentPublicId = entry.realAssetDeploymentPublicId || "";
    return {
      path: deploymentPublicId ? entry.path.slice(1).join("/") || "/" : "/",
      realAssetDeploymentPublicId: deploymentPublicId,
      manifestType: entry.manifestType || ("" as const),
      search: entry.search,
    };
  }, []);

  const navigateToCruise = useCallback(
    (item: FileSystemItem) => {
      if (!item.realAssetDeploymentPublicId) return;

      const folderPath = [item.name];
      const newHistory = [
        ...history.slice(0, historyIndex + 1),
        {
          path: folderPath,
          search: "",
          realAssetDeploymentPublicId: item.realAssetDeploymentPublicId,
        },
      ];
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setCurrentPath(folderPath);
      setSelectedItems(new Set());
      setQueryValues({
        path: "/",
        realAssetDeploymentPublicId: item.realAssetDeploymentPublicId,
        manifestType: "",
        search: "",
      });
    },
    [history, historyIndex, setQueryValues]
  );

  const navigateToFolder = useCallback(
    (folderPath: string[], addToHistory = true) => {
      const currentPathStr = currentPath.join("/");
      const newPathStr = folderPath.join("/");

      if (currentPathStr === newPathStr) {
        return;
      }

      if (addToHistory) {
        const pathQuery = buildPathQuery(folderPath);
        const newHistory = [
          ...history.slice(0, historyIndex + 1),
          {
            path: folderPath,
            search: "",
            realAssetDeploymentPublicId:
              pathQuery.realAssetDeploymentPublicId || undefined,
            manifestType: pathQuery.manifestType || undefined,
          },
        ];
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
      setCurrentPath(folderPath);
      setSelectedItems(new Set());
      setQueryValues({
        ...buildPathQuery(folderPath),
        search: "",
      });
    },
    [history, historyIndex, currentPath, setQueryValues, buildPathQuery]
  );

  const navigateBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const entry = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentPath(entry.path);
      setSelectedItems(new Set());
      setQueryValues(buildHistoryQuery(entry));
    }
  }, [history, historyIndex, setQueryValues, buildHistoryQuery]);

  const navigateForward = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const entry = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentPath(entry.path);
      setSelectedItems(new Set());
      setQueryValues(buildHistoryQuery(entry));
    }
  }, [history, historyIndex, setQueryValues, buildHistoryQuery]);

  const handleItemSelection = useCallback(
    (itemPath: string, event: React.MouseEvent) => {
      event.stopPropagation();
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        if (event.ctrlKey || event.metaKey) {
          if (newSet.has(itemPath)) {
            newSet.delete(itemPath);
          } else {
            newSet.add(itemPath);
          }
        } else if (event.shiftKey && prev.size > 0) {
          newSet.clear();
          newSet.add(itemPath);
        } else {
          newSet.clear();
          newSet.add(itemPath);
        }
        return newSet;
      });
    },
    []
  );

  const getPreviewMeta = useCallback(
    (item: FileSystemItem) => assetIsPreviewable(item),
    []
  );

  const getPreviewUrl = useCallback(
    (item: FileSystemItem) =>
      item.dataAssetPublicId
        ? prepAssetPreviewURL({ public_id: item.dataAssetPublicId })
        : null,
    []
  );

  const getDownloadUrl = useCallback(
    (item: FileSystemItem) =>
      item.dataAssetPublicId
        ? prepAssetDownloadURL({ public_id: item.dataAssetPublicId })
        : null,
    []
  );

  const selectPreviewItem = useCallback((item: FileSystemItem) => {
    setPreviewItem(item);
    setLastOpenedItemKey(getPreviewItemKey(item));
    setIsPreviewOpen(true);
  }, []);

  const handlePreview = useCallback(
    (item: FileSystemItem) => {
      const meta = assetIsPreviewable(item);
      if (!meta.canPreview || !item.dataAssetPublicId) return;
      selectPreviewItem(item);
    },
    [selectPreviewItem]
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
    setIsPreviewOpen(false);
    setPreviewNavigatingDirection(null);
  }, []);

  const goToPreviousPreview = useCallback(() => {
    if (previewNavigatingDirection || previewItemIndex <= 0) {
      return;
    }

    selectPreviewItem(previewableItems[previewItemIndex - 1]);
  }, [
    previewItemIndex,
    previewNavigatingDirection,
    previewableItems,
    selectPreviewItem,
  ]);

  const goToNextPreview = useCallback(async () => {
    if (previewNavigatingDirection || previewItemIndex < 0) {
      return;
    }

    const localCandidate = previewableItems[previewItemIndex + 1];
    if (localCandidate) {
      selectPreviewItem(localCandidate);
      return;
    }

    if (!hasNextFilesPage || isFetchingNextFilesPage) {
      return;
    }

    setPreviewNavigatingDirection("next");

    try {
      const result = await fetchNextFilesPage();
      const fetchedFileItems = (result.data?.pages || []).flatMap((page) =>
        mapFilesToFileItems(page.items || [], currentPath, !!isArchive)
      );
      const nextPreviewableItems = getPreviewableItems(
        sortBrowseItems(applyAdvancedFilters(fetchedFileItems))
      );
      const nextPreviewItemIndex = nextPreviewableItems.findIndex(
        (item) => getPreviewItemKey(item) === getPreviewItemKey(previewItem)
      );
      const nextCandidate = nextPreviewableItems[nextPreviewItemIndex + 1];

      if (nextCandidate) {
        selectPreviewItem(nextCandidate);
      }
    } catch (err) {
      setTreeError("Failed to load next preview");
    } finally {
      setPreviewNavigatingDirection(null);
    }
  }, [
    applyAdvancedFilters,
    currentPath,
    fetchNextFilesPage,
    hasNextFilesPage,
    isFetchingNextFilesPage,
    isArchive,
    previewItem,
    previewItemIndex,
    previewNavigatingDirection,
    previewableItems,
    selectPreviewItem,
    sortBrowseItems,
  ]);

  const handleSeeDeployment = useCallback(
    async (item: FileSystemItem) => {
      if (!item.realAssetDeploymentPublicId || !hasDeploymentFeature) return;
      try {
        const resp = await seeDeploymentMutation.mutateAsync(
          item.realAssetDeploymentPublicId
        );
        const realAssetId =
          resp.real_asset?.public_id || resp.real_asset?.public_id;
        if (realAssetId) {
          navigate({
            to: `/realassets/${realAssetId}/${item.realAssetDeploymentPublicId}`,
          });
        }
      } catch (err) {
        // Error handled in mutation onError.
      }
    },
    [hasDeploymentFeature, navigate, seeDeploymentMutation]
  );

  const previewMeta = useMemo(() => {
    if (!previewItem) {
      return { assetType: "", canPreview: false };
    }
    return getPreviewMeta(previewItem);
  }, [getPreviewMeta, previewItem]);

  const previewUrl = useMemo(() => {
    if (!previewItem) return null;
    return getPreviewUrl(previewItem);
  }, [getPreviewUrl, previewItem]);

  const previewDownloadUrl = useMemo(() => {
    if (!previewItem) return null;
    return getDownloadUrl(previewItem);
  }, [getDownloadUrl, previewItem]);

  const handleItemClick = useCallback(
    (item: FileSystemItem) => {
      if (item.type === "folder") {
        if (item.cruiseId) {
          navigateToCruise(item);
          return;
        }
        const newPath = item.path ? item.path.split("/") : [];
        navigateToFolder(newPath);
      }
    },
    [navigateToCruise, navigateToFolder]
  );

  const handleCreateFolder = useCallback(() => {
    if (newFolderName.trim()) {
      setShowNewFolderModal(false);
      setNewFolderName("");
    }
  }, [newFolderName]);

  const isCurrentFolderRestricted = useCallback(() => {
    if (currentPath.length === 0) return true;
    if (currentPath.length === 1) return true;
    const manifestSegment = currentPath[1] || "";
    if (manifestSegment.includes(ARCHIVE_SUFFIX)) return true;
    return false;
  }, [currentPath]);

  const getCurrentFolderName = useCallback(() => {
    if (currentPath.length === 0) return "Cruises";
    return currentPath[currentPath.length - 1];
  }, [currentPath]);

  const handleOpenAdvancedSearch = useCallback(() => {
    if (!activeFilters) {
      setAdvancedSearchParams({
        searchTerm: searchQuery,
        fileTypes: [],
        dateModified: "any",
        dateFrom: "",
        dateTo: "",
      });
    } else {
      setAdvancedSearchParams({
        searchTerm: searchQuery,
        fileTypes: activeFilters.fileTypes,
        dateModified: activeFilters.dateModified,
        dateFrom: activeFilters.dateFrom,
        dateTo: activeFilters.dateTo,
      });
    }
    setShowAdvancedSearch(true);
  }, [activeFilters, searchQuery]);

  const hasPreviousPreview = previewItemIndex > 0;
  const hasNextPreview =
    previewItemIndex >= 0 &&
    (previewItemIndex < previewableItems.length - 1 || !!hasNextFilesPage);

  return (
    <ContentPage title="Data Asset Browser">
      <BrowsePreviewDialog
        isOpen={isPreviewOpen}
        onClose={closePreview}
        previewItem={previewItem}
        previewMeta={previewMeta}
        previewUrl={previewUrl}
        previewDownloadUrl={previewDownloadUrl}
        hasDeploymentFeature={!!hasDeploymentFeature}
        isOpeningDeployment={isOpeningDeployment}
        onSeeDeployment={handleSeeDeployment}
        onPrevious={goToPreviousPreview}
        onNext={goToNextPreview}
        hasPrevious={hasPreviousPreview}
        hasNext={hasNextPreview}
        navigatingDirection={previewNavigatingDirection}
      />
      <div className="flex h-full flex-col bg-zinc-50 text-gray-900">
        <BrowseHeader
          currentPath={currentPath}
          historyIndex={historyIndex}
          historyLength={history.length}
          onNavigateBack={navigateBack}
          onNavigateForward={navigateForward}
          onNavigateToFolder={navigateToFolder}
        />

        {treeError && (
          <div className="px-4 py-2 text-sm text-red-600">{treeError}</div>
        )}

        <div className="flex flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedItems(new Set());
              }
            }}
          >
            <div
              className="p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setSelectedItems(new Set());
                }
              }}
            >
              <BrowseControls
                searchQuery={searchQuery}
                getCurrentFolderName={getCurrentFolderName}
                onSearchChange={(value) => setQueryValues({ search: value })}
                onClearSearch={() => setQueryValues({ search: "" })}
                onOpenAdvancedSearch={handleOpenAdvancedSearch}
                activeFilters={activeFilters}
                onClearFileType={(fileType) => {
                  const nextFileTypes = (activeFilters?.fileTypes || []).filter(
                    (type) => type !== fileType
                  );
                  setQueryValues({
                    fileTypes: nextFileTypes,
                    search: searchQuery,
                  });
                }}
                onClearDateModified={() =>
                  setQueryValues({
                    dateModified: "any",
                    dateFrom: "",
                    dateTo: "",
                    search: searchQuery,
                  })
                }
                onClearAllFilters={() => {
                  setAdvancedSearchParams({
                    searchTerm: "",
                    fileTypes: [],
                    dateModified: "any",
                    dateFrom: "",
                    dateTo: "",
                  });
                  setQueryValues({
                    search: "",
                    fileTypes: [],
                    dateModified: "any",
                    dateFrom: "",
                    dateTo: "",
                  });
                }}
                isCurrentFolderRestricted={isCurrentFolderRestricted()}
                selectedItemsCount={selectedItems.size}
                onDeleteSelected={() => setShowDeleteModal(true)}
                onNewFolder={() => setShowNewFolderModal(true)}
                onUploadFile={() => setShowUploadManager(true)}
                onUploadFolder={() => setShowUploadManager(true)}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSelectSortBy={(option) => {
                  setSortBy(option);
                  if (option !== "none" && sortBy === "none") {
                    setSortOrder("asc");
                  }
                }}
                onSelectSortOrder={(order) => {
                  setSortOrder(order);
                }}
              />

              <BrowseItems
                viewMode={viewMode}
                itemsByYear={itemsByYear}
                selectedItems={selectedItems}
                onItemSelection={handleItemSelection}
                onItemClick={handleItemClick}
                onPreview={handlePreview}
                onSeeDeployment={handleSeeDeployment}
                getPreviewMeta={getPreviewMeta}
                getDownloadUrl={getDownloadUrl}
                hasDeploymentFeature={!!hasDeploymentFeature}
                isOpeningDeployment={isOpeningDeployment}
                currentPath={currentPath}
                searchQuery={searchQuery}
                debouncedSearchQuery={debouncedSearchQuery}
                hasActiveFilters={hasActiveFilters}
                hoveredIcon={hoveredIcon}
                setHoveredIcon={setHoveredIcon}
                onClearSelection={() => setSelectedItems(new Set())}
                isFetchingNodes={isFetchingNodes}
                isLoadingMore={isLoadingMore}
                loadMoreSentinelRef={loadMoreSentinelRef}
                lastOpenedItemKey={lastOpenedItemKey}
              />
            </div>
          </div>
        </div>

        <BrowseAdvancedSearchModal
          show={showAdvancedSearch}
          advancedSearchParams={advancedSearchParams}
          setAdvancedSearchParams={setAdvancedSearchParams}
          onClose={() => setShowAdvancedSearch(false)}
          onReset={() => {
            const resetParams = {
              searchTerm: "",
              fileTypes: [],
              dateModified: "any",
              dateFrom: "",
              dateTo: "",
            };
            setAdvancedSearchParams(resetParams);
          }}
          onSearch={() => {
            setQueryValues({
              search: advancedSearchParams.searchTerm,
              fileTypes: advancedSearchParams.fileTypes,
              dateModified: advancedSearchParams.dateModified,
              dateFrom: advancedSearchParams.dateFrom,
              dateTo: advancedSearchParams.dateTo,
            });
            setShowAdvancedSearch(false);
          }}
        />

        <BrowseNewFolderModal
          show={showNewFolderModal}
          newFolderName={newFolderName}
          onChange={setNewFolderName}
          onClose={() => {
            setShowNewFolderModal(false);
            setNewFolderName("");
          }}
          onCreate={handleCreateFolder}
        />

        <BrowseDeleteModal
          show={showDeleteModal}
          selectedItems={selectedItems}
          filteredAndSortedItems={filteredAndSortedItems}
          deleteConfirmationChecked={deleteConfirmationChecked}
          onToggleConfirmation={setDeleteConfirmationChecked}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteConfirmationChecked(false);
          }}
          onConfirm={() => {
            setSelectedItems(new Set());
            setShowDeleteModal(false);
            setDeleteConfirmationChecked(false);
          }}
        />

        {showUploadManager && (
          <FileUploadManager
            currentPath={currentPath.join("/")}
            onClose={(files) => {
              setShowUploadManager(false);

              if (files && files.length > 0) {
                const successfulUploads = files.filter(
                  (f) => f.status === "done"
                );
                const newTrackedUploads: TrackedUpload[] =
                  successfulUploads.map((file) => ({
                    id: file.id,
                    fileName: file.name,
                    fileSize: file.size,
                    stage: "processing" as UploadStage,
                    progress: 0,
                    startTime: new Date(),
                  }));

                setTrackedUploads((prev) => [...prev, ...newTrackedUploads]);

                newTrackedUploads.forEach((upload, index) => {
                  const shouldFail = (index + 1) % 3 === 0;

                  const attemptProcessing = (retryCount = 0) => {
                    const processingTime = 2000 + Math.random() * 3000;

                    setTimeout(() => {
                      if (shouldFail && retryCount < 3) {
                        attemptProcessing(retryCount + 1);
                      } else if (shouldFail && retryCount === 3) {
                        setTrackedUploads((prev) =>
                          prev.map((u) =>
                            u.id === upload.id
                              ? {
                                  ...u,
                                  stage: "error" as UploadStage,
                                  error: "Processing failed after 3 retries",
                                  retryCount: 3,
                                  maxRetries: 3,
                                  endTime: new Date(),
                                }
                              : u
                          )
                        );
                      } else {
                        setTrackedUploads((prev) =>
                          prev.map((u) =>
                            u.id === upload.id
                              ? {
                                  ...u,
                                  stage: "completed" as UploadStage,
                                  endTime: new Date(),
                                }
                              : u
                          )
                        );
                      }
                    }, processingTime);
                  };

                  attemptProcessing();
                });
              }
            }}
            onUploadComplete={(files) => {
              console.log("Upload completed:", files);
            }}
          />
        )}

        <UploadProgressTracker
          uploads={trackedUploads}
          onRemoveUpload={(id) => {
            setTrackedUploads((prev) => prev.filter((u) => u.id !== id));
          }}
        />
      </div>
    </ContentPage>
  );
}
